import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Search, Loader2, Download, Globe, Phone, Mail, MapPin, Trash2, ChevronDown,
  Info, RefreshCw, Building2, Sparkles, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminScrape, adminScrapedLeads, adminUpdateScrapedLead, adminDeleteScrapedLead, adminExportScrapedLeads,
} from "../lib/api";

const BRANCHEN = [
  { slug: "hotels", label: "Hotels" },
  { slug: "restaurants", label: "Restaurants & Gastronomie" },
  { slug: "hausverwaltungen", label: "Hausverwaltungen" },
  { slug: "arztpraxen", label: "Arztpraxen" },
  { slug: "zahnaerzte", label: "Zahnärzte" },
  { slug: "handwerk", label: "Handwerksbetriebe" },
  { slug: "autohaeuser", label: "Autohäuser" },
  { slug: "fitnessstudios", label: "Fitnessstudios" },
  { slug: "einzelhandel", label: "Einzelhandel" },
  { slug: "produktionsbetriebe", label: "Produktionsbetriebe" },
  { slug: "industrie", label: "Industrie" },
  { slug: "immobilienunternehmen", label: "Immobilienunternehmen" },
];

const STATES = ["Baden-Württemberg", "Bayern", "Berlin", "Brandenburg", "Bremen", "Hamburg", "Hessen",
  "Mecklenburg-Vorpommern", "Niedersachsen", "Nordrhein-Westfalen", "Rheinland-Pfalz", "Saarland",
  "Sachsen", "Sachsen-Anhalt", "Schleswig-Holstein", "Thüringen"];

const STATUSES = [
  { key: "neu", label: "Neu", color: "bg-blue-100 text-blue-700" },
  { key: "zu_pruefen", label: "Zu prüfen", color: "bg-indigo-100 text-indigo-700" },
  { key: "kontaktiert", label: "Kontaktiert", color: "bg-amber-100 text-amber-700" },
  { key: "angebot", label: "Angebot erstellt", color: "bg-purple-100 text-purple-700" },
  { key: "kunde", label: "Kunde geworden", color: "bg-emerald-100 text-emerald-700" },
  { key: "kein_interesse", label: "Kein Interesse", color: "bg-slate-200 text-slate-600" },
];
const STATUS_LABEL = Object.fromEntries(STATUSES.map((s) => [s.key, s.label]));

const CAT = {
  Hoch: "bg-rose-50 text-rose-700 border-rose-200",
  Mittel: "bg-amber-50 text-amber-700 border-amber-200",
  Niedrig: "bg-slate-100 text-slate-600 border-slate-200",
};

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return "—"; } };

const ResearchCard = ({ lead, onUpdate, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [notiz, setNotiz] = useState(lead.notizen || "");
  const cat = CAT[lead.category] || CAT.Niedrig;
  const overdue = lead.wiedervorlage && new Date(lead.wiedervorlage) <= new Date();

  const patch = (updates) => onUpdate(lead.id, updates);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="research-card">
      <div className="flex items-start gap-4">
        <div className="shrink-0 text-center">
          <div className={`h-12 w-12 rounded-full border-2 grid place-items-center font-heading text-lg ${cat}`} data-testid="research-score">{lead.score}</div>
          <p className="text-[10px] text-slate-400 mt-1">{lead.category}</p>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="font-heading text-navy-900 text-base truncate">{lead.firmenname}</span>
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{lead.branche}</span>
            {lead.is_duplicate && (
              <span title={lead.duplicate_reason} className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1" data-testid="research-duplicate-badge">
                <CheckCircle2 className="h-3 w-3" /> Bereits Anfrage
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mb-2 flex items-center gap-1"><MapPin className="h-3 w-3" /> {[lead.adresse, lead.plz, lead.ort].filter(Boolean).join(", ") || "Adresse unbekannt"}</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {lead.telefon && <a href={`tel:${lead.telefon}`} className="inline-flex items-center gap-1.5 text-xs rounded-full border border-slate-200 px-3 py-1.5 hover:bg-slate-50 text-navy-900" data-testid="research-phone"><Phone className="h-3.5 w-3.5" /> {lead.telefon}</a>}
            {lead.email && <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1.5 text-xs rounded-full border border-slate-200 px-3 py-1.5 hover:bg-slate-50 text-navy-900" data-testid="research-email"><Mail className="h-3.5 w-3.5" /> {lead.email}</a>}
            {lead.website && <a href={lead.website.startsWith("http") ? lead.website : `https://${lead.website}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs rounded-full border border-slate-200 px-3 py-1.5 hover:bg-slate-50 text-blue-700" data-testid="research-website"><Globe className="h-3.5 w-3.5" /> Website</a>}
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {lead.score_reasons?.map((r, i) => (
              <span key={i} className="text-[11px] px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">{r}</span>
            ))}
          </div>

          {/* Pipeline controls */}
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-xs text-slate-500">Status
              <select value={lead.status} onChange={(e) => patch({ status: e.target.value })} className="mt-1 w-full text-sm rounded-lg border border-slate-200 px-2 py-2 outline-none focus:border-blue-400 bg-white" data-testid="research-status">
                {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </label>
            <label className="text-xs text-slate-500">Zuständig
              <select value={lead.mitarbeiter || "Kerstin"} onChange={(e) => patch({ mitarbeiter: e.target.value })} className="mt-1 w-full text-sm rounded-lg border border-slate-200 px-2 py-2 outline-none focus:border-blue-400 bg-white" data-testid="research-mitarbeiter">
                <option value="Kerstin">Kerstin</option>
                <option value="—">— nicht zugewiesen</option>
              </select>
            </label>
            <label className="text-xs text-slate-500">Letzter Kontakt
              <input type="date" value={lead.letzter_kontakt || ""} onChange={(e) => patch({ letzter_kontakt: e.target.value })} className="mt-1 w-full text-sm rounded-lg border border-slate-200 px-2 py-2 outline-none focus:border-blue-400" data-testid="research-lastcontact" />
            </label>
            <label className="text-xs text-slate-500">Wiedervorlage {overdue && <span className="text-rose-600 font-medium">· fällig</span>}
              <input type="date" value={lead.wiedervorlage || ""} onChange={(e) => patch({ wiedervorlage: e.target.value })} className={`mt-1 w-full text-sm rounded-lg border px-2 py-2 outline-none focus:border-blue-400 ${overdue ? "border-rose-300 bg-rose-50" : "border-slate-200"}`} data-testid="research-followup" />
            </label>
          </div>

          <button onClick={() => setOpen((o) => !o)} className="mt-3 inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-navy-900" data-testid="research-toggle-notes">
            Notizen & Quelle <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          {open && (
            <div className="mt-3 space-y-3">
              <textarea value={notiz} onChange={(e) => setNotiz(e.target.value)} onBlur={() => notiz !== (lead.notizen || "") && patch({ notizen: notiz })}
                placeholder="Notiz zur Anfrage…" rows={3}
                className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 outline-none focus:border-blue-400" data-testid="research-notes" />
              <div className="flex items-center justify-between text-[11px] text-slate-400">
                <span>Quelle: {lead.quelle}{lead.email_quelle ? ` · E-Mail: ${lead.email_quelle}` : ""} · erfasst {fmtDate(lead.erfasst_am)}</span>
                <button onClick={() => onDelete(lead.id)} className="inline-flex items-center gap-1 text-rose-500 hover:text-rose-700" data-testid="research-delete"><Trash2 className="h-3.5 w-3.5" /> Entfernen</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const LeadResearch = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [selBranchen, setSelBranchen] = useState(["hotels"]);
  const [stadt, setStadt] = useState("");
  const [bundesland, setBundesland] = useState("");
  const [umkreis, setUmkreis] = useState(10);
  const [nurKontakt, setNurKontakt] = useState(true);
  const [enrich, setEnrich] = useState(true);
  const [statusFilter, setStatusFilter] = useState("alle");
  const [brancheFilter, setBrancheFilter] = useState("alle");
  const [q, setQ] = useState("");
  const [hideDup, setHideDup] = useState(false);

  const load = async () => { setLeads(await adminScrapedLeads()); };
  useEffect(() => { load().finally(() => setLoading(false)); }, []);

  const toggleBranche = (slug) => setSelBranchen((p) => p.includes(slug) ? p.filter((x) => x !== slug) : [...p, slug]);

  const runScrape = async () => {
    if (selBranchen.length === 0) { toast.error("Bitte mindestens eine Branche wählen."); return; }
    if (!stadt && !bundesland) { toast.error("Bitte Stadt oder Bundesland angeben."); return; }
    setScraping(true);
    try {
      const res = await adminScrape({ branchen: selBranchen, stadt, bundesland, umkreis_km: umkreis, nur_mit_kontakt: nurKontakt, enrich_email: enrich });
      toast.success(`${res.neu} neue Leads gefunden (${res.gefunden} geprüft) – ${res.region}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Recherche fehlgeschlagen.");
    } finally {
      setScraping(false);
    }
  };

  const update = async (id, updates) => {
    setLeads((p) => p.map((l) => (l.id === id ? { ...l, ...updates } : l)));
    try { await adminUpdateScrapedLead(id, updates); } catch { toast.error("Speichern fehlgeschlagen."); load(); }
  };
  const remove = async (id) => {
    setLeads((p) => p.filter((l) => l.id !== id));
    try { await adminDeleteScrapedLead(id); } catch { load(); }
  };
  const exportCsv = async () => {
    try { await adminExportScrapedLeads(); toast.success("CSV-Export gestartet."); }
    catch { toast.error("Export fehlgeschlagen."); }
  };

  const statusCounts = useMemo(() => {
    const c = {};
    STATUSES.forEach((s) => (c[s.key] = 0));
    leads.forEach((l) => (c[l.status] = (c[l.status] || 0) + 1));
    return c;
  }, [leads]);

  const filtered = useMemo(() => leads.filter((l) => {
    if (statusFilter !== "alle" && l.status !== statusFilter) return false;
    if (brancheFilter !== "alle" && l.branche_slug !== brancheFilter) return false;
    if (hideDup && l.is_duplicate) return false;
    if (q) { const s = `${l.firmenname} ${l.ort} ${l.email}`.toLowerCase(); if (!s.includes(q.toLowerCase())) return false; }
    return true;
  }), [leads, statusFilter, brancheFilter, q, hideDup]);

  const dupCount = useMemo(() => leads.filter((l) => l.is_duplicate).length, [leads]);

  return (
    <div data-testid="lead-research">
      {/* DSGVO note */}
      <div className="rounded-2xl border border-blue-100 bg-blue-50/60 p-4 mb-6 flex gap-3" data-testid="research-legal-note">
        <Info className="h-4 w-4 text-blue-700 shrink-0 mt-0.5" />
        <p className="text-xs text-slate-600 leading-relaxed">
          Es werden ausschließlich <strong>öffentlich zugängliche Geschäftsdaten</strong> (Quelle: OpenStreetMap; E-Mail ggf. aus dem Impressum der Firmenwebsite) recherchiert. Keine privaten Kontaktdaten. Eine Kontaktaufnahme erfolgt <strong>erst nach manueller Freigabe</strong> – keine automatischen Werbe-E-Mails oder ‑anrufe (DSGVO & Wettbewerbsrecht).
        </p>
      </div>

      {/* Search panel */}
      <div className="rounded-3xl border border-slate-200 bg-white p-6 mb-8" data-testid="research-search-panel">
        <p className="text-xs uppercase tracking-[0.2em] text-blue-700 mb-4 inline-flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" /> Neue Leads recherchieren</p>
        <div className="mb-4">
          <p className="text-xs text-slate-500 mb-2">Branchen</p>
          <div className="flex flex-wrap gap-2" data-testid="research-branchen">
            {BRANCHEN.map((b) => (
              <button key={b.slug} onClick={() => toggleBranche(b.slug)} data-testid={`research-branche-${b.slug}`}
                className={`text-sm rounded-full px-3.5 py-1.5 border transition-colors ${selBranchen.includes(b.slug) ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                {b.label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-4 mb-4">
          <label className="text-xs text-slate-500">Stadt
            <input value={stadt} onChange={(e) => setStadt(e.target.value)} placeholder="z. B. Hamburg" className="mt-1 w-full text-sm rounded-lg border border-slate-200 px-3 py-2.5 outline-none focus:border-blue-400" data-testid="research-stadt" />
          </label>
          <label className="text-xs text-slate-500">Bundesland (optional)
            <select value={bundesland} onChange={(e) => setBundesland(e.target.value)} className="mt-1 w-full text-sm rounded-lg border border-slate-200 px-3 py-2.5 outline-none focus:border-blue-400 bg-white" data-testid="research-bundesland">
              <option value="">— alle</option>
              {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="text-xs text-slate-500">Umkreis: <strong>{umkreis} km</strong>
            <input type="range" min="1" max="50" value={umkreis} onChange={(e) => setUmkreis(Number(e.target.value))} className="mt-3 w-full accent-blue-700" data-testid="research-umkreis" />
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-4 mb-5">
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input type="checkbox" checked={nurKontakt} onChange={(e) => setNurKontakt(e.target.checked)} className="accent-blue-700 h-4 w-4" data-testid="research-nurkontakt" />
            Nur Betriebe mit Website/Telefon
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input type="checkbox" checked={enrich} onChange={(e) => setEnrich(e.target.checked)} className="accent-blue-700 h-4 w-4" data-testid="research-enrich" />
            E-Mail aus Impressum ermitteln <span className="text-slate-400">(dauert etwas länger)</span>
          </label>
        </div>
        <button onClick={runScrape} disabled={scraping} className="inline-flex items-center gap-2 rounded-full bg-blue-700 hover:bg-blue-600 disabled:opacity-60 text-white px-6 py-3 font-medium transition-colors" data-testid="research-run">
          {scraping ? <><Loader2 className="h-4.5 w-4.5 animate-spin" /> Recherchiere…</> : <><Search className="h-4.5 w-4.5" /> Leads suchen</>}
        </button>
        {scraping && <p className="text-xs text-slate-400 mt-2">Öffentliche Daten werden recherchiert – das kann bis zu einer Minute dauern.</p>}
      </div>

      {/* Pipeline overview */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="research-pipeline">
        {STATUSES.map((s) => (
          <button key={s.key} onClick={() => setStatusFilter(statusFilter === s.key ? "alle" : s.key)}
            className={`rounded-2xl border p-4 text-left transition-colors ${statusFilter === s.key ? "border-blue-500 bg-blue-50/60" : "border-slate-200 bg-white hover:bg-slate-50"}`} data-testid={`pipeline-${s.key}`}>
            <p className="text-2xl font-heading font-semibold text-navy-900">{statusCounts[s.key] || 0}</p>
            <p className={`text-[11px] mt-1 inline-block px-2 py-0.5 rounded-full ${s.color}`}>{s.label}</p>
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <button onClick={() => setStatusFilter("alle")} className={`text-sm rounded-full px-4 py-2 font-medium ${statusFilter === "alle" ? "bg-navy-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid="research-filter-alle">Alle ({leads.length})</button>
        <select value={brancheFilter} onChange={(e) => setBrancheFilter(e.target.value)} className="text-sm rounded-full border border-slate-200 px-4 py-2 bg-white outline-none" data-testid="research-filter-branche">
          <option value="alle">Alle Branchen</option>
          {BRANCHEN.map((b) => <option key={b.slug} value={b.slug}>{b.label}</option>)}
        </select>
        {dupCount > 0 && (
          <button onClick={() => setHideDup((v) => !v)} className={`inline-flex items-center gap-1.5 text-sm rounded-full px-4 py-2 border transition-colors ${hideDup ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`} data-testid="research-filter-dup">
            <CheckCircle2 className="h-3.5 w-3.5" /> {hideDup ? "Dubletten ausgeblendet" : `${dupCount} bereits Anfrage`}
          </button>
        )}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suche Firma, Ort, E-Mail…" className="bg-white border border-slate-200 rounded-full pl-9 pr-4 py-2 text-sm outline-none focus:border-blue-400 w-60" data-testid="research-search-input" />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={load} className="inline-flex items-center gap-1.5 text-sm text-slate-600 rounded-full border border-slate-200 px-3 py-2 hover:bg-slate-50" data-testid="research-refresh"><RefreshCw className="h-3.5 w-3.5" /> Aktualisieren</button>
          <button onClick={exportCsv} className="inline-flex items-center gap-1.5 text-sm rounded-full bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2" data-testid="research-export"><Download className="h-3.5 w-3.5" /> CSV-Export</button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="grid place-items-center py-16"><Loader2 className="h-7 w-7 animate-spin text-blue-700" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-16 text-center text-slate-400" data-testid="research-empty">
          <Building2 className="h-8 w-8 mx-auto mb-3 opacity-40" />
          {leads.length === 0 ? "Noch keine Leads – starte oben eine Recherche." : "Keine Leads für diesen Filter."}
        </div>
      ) : (
        <div className="space-y-4" data-testid="research-list">
          {filtered.map((l) => <ResearchCard key={l.id} lead={l} onUpdate={update} onDelete={remove} />)}
        </div>
      )}
    </div>
  );
};
